import { FetchResult } from '@apollo/client/link/core'
import {
  Form,
  useForm
} from '@cedarjs/forms'
import { routes, useParams } from "@cedarjs/router"
import { faCheckCircle, faWarning } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Alert, Button, Divider, MenuItem, Paper, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { t } from "i18next"
import { useAuth } from 'src/auth'
import { ControllerDecimal, ControllerInt, ControllerSelect, ControllerTextArea } from "src/components/Controllers/Controllers"
import { renderBiomass, renderQuantity } from "src/components/ForecastCell"
import { MODELS_ACTIONS_ANALYSES } from "src/const/Models"
import { convertToFloatWithLowPrecision, roundFloat } from "src/const/Units"
import { useDateStore } from "src/lib/store"
import type { CreateReviewCommentInput, Tank, UpdateReviewCommentInput } from 'types/graphql'
import { FindEditReviewCommentQuery } from 'types/graphql'
import { ReviewCommentContent, ReviewContent } from "../ReviewCard/ReviewCard"
import { hasResourcePermission } from "src/lib/roles"
import { useEffect } from "react"

//Let id be optional
type FormReviewComment = Omit<FindEditReviewCommentQuery['review'], 'id'> & { id?: number }

interface ReviewCommentFormProps {
  reviewComment?: FindEditReviewCommentQuery['reviewComment']
  parent?: FindEditReviewCommentQuery['reviewComment']
  onSave: (data: UpdateReviewCommentInput | CreateReviewCommentInput, id?: FormReviewComment['id']) => Promise<FetchResult>
  className?: string
  isInline?: boolean
  review?: FindEditReviewCommentQuery['review']
}

const TYPE_OPTIONS = [
  "preco",
  "comment",
]
const ReviewCommentForm = (props: ReviewCommentFormProps) => {
  //Retrieve the current selected date
  const date = useDateStore(state => state.date)
  const { hasRole } = useAuth()
  //If we are in the context of a selected tank, we'll block the tank field edition
  const { idTank } = useParams();
  const { control, handleSubmit, watch, resetField, reset, setError, setValue, formState: { errors, isSubmitted, isSubmitting, isValid, isSubmitSuccessful }, register } = useForm<
  {
    idReview:number
    idReviewComment:number
    content:string
    type:string
    models:string[]
  }
  >({
    defaultValues: {
      idReview: props.reviewComment?.idReview || props.parent?.idReview || props.review?.id,
      idReviewComment: props.parent?.id,
      content: props.reviewComment?.content,
      type: props.reviewComment?.type || "comment",
      models: props.reviewComment?.models,
    },
  });

  //To retrieve the sub
  const { currentUser } = useAuth()

  const onSubmit = async (data: FormReviewComment) => {
    const result = await props.onSave(data, props?.reviewComment?.id)
    if (!result.errors) {
      reset()
      return true
    }
    else {
      //@ts-expect-error TODO: Can't find the right type of the result
      setError("root", { type: "server", message: result.errors.message })
      return false
    }
  }

  const models:string[] = watch("models")
  useEffect(()=> {
    if(models?.length)
      setValue("type", 'preco' as string)
    else
      setValue("type", 'comment' as string)
  }, models)
  
  const type:string = watch("type")

  if(!hasResourcePermission(hasRole, "ReviewComment", "create"))
    return;
  
  const _Form = (
    <Form onSubmit={handleSubmit(onSubmit)} className='flex flex-col space-y-4' >
      {hasResourcePermission(hasRole, "Review", "create") && !props.parent &&(
        <div className="flex gap-2 items-center">
          {/* <ControllerSelect
            name="type"
            className="grow"
            size="small"
            error={errors.type}
            control={control}
            required
          >

            {TYPE_OPTIONS.map((type, index) => (
              <MenuItem value={type} key={type}>
                <span>{t(`labels.${type}`, type)}</span>
              </MenuItem>
            ))}
          </ControllerSelect> */}
          
            <ControllerSelect
              name="models"
              label={"preconisationFor"}
              className="grow"
              size="small"
              error={errors.models}
              control={control}
              multiple
            >
              {MODELS_ACTIONS_ANALYSES.map((model, index) => (
                <MenuItem value={model} key={model}>
                  <span>{t(`models.${model}`, model)}</span>
                </MenuItem>
              ))}

            </ControllerSelect>

        </div>
      )}
      <ControllerTextArea
        name="content"
        label={type == "preco" ? "reviewPreconisation" : "reviewComment"}
        error={errors.content}
        control={control}
        placeholder={type == "preco" ? t('placeholders.preconisation') : t('placeholders.description')}
        required
      />
      

      {/* <ControllerText control={control} name="createdBy" setValue={setValue} error={errors.createdBy} required className="hidden"/> */}
      {isSubmitted && !isValid && !isSubmitSuccessful && <Alert severity="error" className="whitespace-pre-line">{Object.values(errors).map((field, index) => <div key={index}>{field.message}</div>)}</Alert>}
      {isSubmitSuccessful && <Alert severity="success">{(type == "preco" ? t('labels.reviewPreconisation') : t('labels.reviewComment') )+ t('status.saved')}</Alert>}
      <div className="flex justify-between gap-2">
        <Button type="submit" variant="contained" disabled={isSubmitting}>{props.reviewComment?.id ? t('actions.edit') : t('actions.save') + ' ' + (type == "preco" ? t('labels.reviewPreconisation') : t('labels.reviewComment') )}</Button>
      </div>
    </Form>
  )
  if (!props.isInline)
    return <Paper elevation={2} className='m-2 p-5'>
      <Typography variant='h2' className="pb-6">{`${props.reviewComment?.id ? t('actions.edit') : t('status.new', { context: "m" })} ${t('labels.reviewComment').toLocaleLowerCase()}`}</Typography>
      <div className="flex mb-4 items-center">
        {props.review && <ReviewContent review={props.review} />}
        {props.parent && <ReviewCommentContent reviewComment={props.parent} />}
      </div>
      {_Form}
    </Paper>
  else
    return _Form
}

export default ReviewCommentForm
